import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { CATEGORIES, STATUSES } from "@/lib/leadMeta";
import { downloadBlob } from "@/lib/download";

const money = (n) => (n == null ? "" : `$${Number(n).toLocaleString("en-US")}`);
const products = (l) => {
  const list = l.insurance_types?.length ? l.insurance_types : [l.insurance_type].filter(Boolean);
  return list.map((t) => CATEGORIES[t]?.label || t).join(", ");
};
const statusLabel = (l) => STATUSES[l.status]?.label || l.status || "";

export const COLUMNS = [
  ["Name", (l) => l.name || ""],
  ["Phone", (l) => l.phone || ""],
  ["Email", (l) => l.email || ""],
  ["Company", (l) => l.company || ""],
  ["Address", (l) => l.address || ""],
  ["Address 2", (l) => l.address2 || ""],
  ["City", (l) => l.city || ""],
  ["State", (l) => l.state || ""],
  ["ZIP", (l) => l.zip || ""],
  ["Products", products],
  ["Status", statusLabel],
  ["Assigned", (l) => l.assigned_to_name || "Unassigned"],
  ["Premium", (l) => money(l.premium_estimate)],
  ["Commission %", (l) => (l.commission_pct == null || l.commission_pct === "" ? "" : `${l.commission_pct}%`)],
  ["Projected Commission", (l) => money((Number(l.premium_estimate) || 0) * (Number(l.commission_pct) || 0) / 100)],
];

const rows = (leads) => leads.map((l) => Object.fromEntries(COLUMNS.map(([h, f]) => [h, f(l)])));
const stamp = () => new Date().toISOString().slice(0, 10);
const slug = (s) => (s || "leads").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : [13, 148, 136];
};

const loadImageDataUrl = (url) => new Promise((resolve) => {
  if (!url) return resolve(null);
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
    } catch { resolve(null); }
  };
  img.onerror = () => resolve(null);
  img.src = url;
});

export const exportExcel = (leads, brand = {}) => {
  const ws = XLSX.utils.json_to_sheet(rows(leads));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Leads");
  const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  downloadBlob(out, `${slug(brand.name)}-leads-${stamp()}.xlsx`,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
};

export const exportPDF = async (leads, brand = {}) => {
  const [r, g, b] = hexToRgb(brand.color);
  const name = brand.name || "IShareLeads";
  const doc = new jsPDF({ orientation: "landscape" });
  const pw = doc.internal.pageSize.getWidth();
  doc.setFillColor(r, g, b);
  doc.rect(0, 0, pw, 22, "F");
  const logo = await loadImageDataUrl(brand.logoUrl);
  let textX = 14;
  if (logo) {
    const h = 12, w = Math.min(24, (logo.w / logo.h) * h);
    try { doc.addImage(logo.dataUrl, "PNG", 12, 5, w, h); textX = 12 + w + 6; } catch {}
  }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(15);
  doc.text(`${name} — Leads`, textX, brand.address ? 12 : 14);
  if (brand.address) { doc.setFontSize(9); doc.text(String(brand.address), textX, 18); }
  autoTable(doc, {
    startY: 28,
    head: [COLUMNS.map(([h]) => h)],
    body: leads.map((l) => COLUMNS.map(([, f]) => f(l))),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [r, g, b] },
  });
  const y = doc.lastAutoTable.finalY || 28;
  doc.setTextColor(120, 120, 120);
  doc.setFontSize(9);
  doc.text(`${leads.length} lead(s) · ${new Date().toLocaleString("en-US")}`, 14, Math.min(y + 8, doc.internal.pageSize.getHeight() - 8));
  downloadBlob(doc.output("blob"), `${slug(name)}-leads-${stamp()}.pdf`, "application/pdf");
};

export const printLeads = (leads, brand = {}) => {
  const esc = (s) => String(s).replace(/</g, "&lt;");
  const color = brand.color || "#0D9488";
  const name = brand.name || "IShareLeads";
  const head = COLUMNS.map(([h]) => `<th>${h}</th>`).join("");
  const body = leads.map((l) => `<tr>${COLUMNS.map(([, f]) => `<td>${esc(f(l))}</td>`).join("")}</tr>`).join("");
  const logo = brand.logoUrl ? `<img src="${brand.logoUrl}" style="height:36px;width:auto;object-fit:contain" />` : "";
  const html = `<!doctype html><html><head><title>${esc(name)} — Leads</title><style>
    body{font-family:system-ui,Arial,sans-serif;padding:0;color:#111}
    .hdr{background:${color};color:#fff;padding:16px 24px;display:flex;align-items:center;gap:12px}
    .hdr h1{font-size:18px;margin:0}
    .wrap{padding:24px} p.meta{color:#555;margin:0 0 16px;font-size:12px}
    table{width:100%;border-collapse:collapse;font-size:11px}
    th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}
    thead th{background:${color};color:#fff}
    tr:nth-child(even) td{background:#f6f6f6}
  </style></head><body>
    <div class="hdr">${logo}<div><h1>${esc(name)} — Leads</h1>${brand.address ? `<div style="font-size:12px;opacity:.9;margin-top:2px">${esc(brand.address)}</div>` : ""}</div></div>
    <div class="wrap"><p class="meta">${leads.length} lead(s) · ${new Date().toLocaleString("en-US")}</p>
    <table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>
    <script>window.onload=function(){window.print();}<\/script>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
};
